import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  useTheme,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { fetchAccounts, fetchAccountSummary } from '../../redux/slices/accountsSlice';
import { ChangelogEntry } from '../../types';
import { formatDate } from '../../utils/helpers';
import PageHeader from '../../components/common/PageHeader';
import LoadingScreen from '../../components/common/LoadingScreen';
import ErrorMessage from '../../components/common/ErrorMessage';
import { apiService } from '../../services/api';

const Home: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { summary, isLoading, error } = useAppSelector((state) => state.accounts);
  const [changelog, setChangelog] = React.useState<ChangelogEntry[]>([]);
  const [changelogLoading, setChangelogLoading] = React.useState(true);
  const [changelogError, setChangelogError] = React.useState<string | null>(null);

  // Fetch account data when user changes
  useEffect(() => {
    // Only fetch account data if user has credit_control permission
    if (user?.permissions?.includes('credit_control')) {
      console.log('Fetching account data...');
      dispatch(fetchAccounts());
      dispatch(fetchAccountSummary());
    }
  }, [dispatch, user]);

  const getChipColor = (type: string) => {
    switch (type) {
      case 'feature':
        return {
          bg: theme.palette.success.main + '20',
          color: theme.palette.success.main,
        };
      case 'improvement':
        return {
          bg: theme.palette.info.main + '20',
          color: theme.palette.info.main,
        };
      case 'bugfix':
        return {
          bg: theme.palette.warning.main + '20',
          color: theme.palette.warning.main,
        };
      case 'release':
        return {
          bg: theme.palette.primary.main + '20',
          color: theme.palette.primary.main,
        };
      default:
        return {
          bg: theme.palette.grey[500] + '20',
          color: theme.palette.grey[500],
        };
    }
  };

  // Add a useEffect to fetch changelog data on component mount
  useEffect(() => {
    const fetchChangelogData = async () => {
      try {
        setChangelogLoading(true);
        const data = await apiService.getChangelog();
        setChangelog(data);
        setChangelogError(null);
      } catch (error) {
        console.error('Error fetching changelog data:', error);
        setChangelogError('Failed to load changelog data');
      } finally {
        setChangelogLoading(false);
      }
    };

    fetchChangelogData();
  }, []); // Empty dependency array means this runs once on mount

  if (changelogLoading && !changelog.length) {
    return <LoadingScreen />;
  }

  if (changelogError) {
    return (
      <ErrorMessage
        title="Error Loading Data"
        message={changelogError}
        onRetry={() => window.location.reload()}
        fullPage
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title="Welcome to ToolBox"
        subtitle={`Hello, ${user?.firstName || 'User'}! Here's what's new in the system.`}
      />

      {/* Credit Control Quick Access (only if user has permission) */}
      {user?.permissions?.includes('credit_control') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Credit Control Overview
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Account Summary
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Active Accounts
                  </Typography>
                  <Typography variant="h5">
                    {summary?.totalActiveAccounts || 0}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/credit-control')}
                >
                  Go to Credit Control
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                {isLoading ? (
                  <Typography>Loading...</Typography>
                ) : error ? (
                  <Typography color="error">Error loading data</Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Accounts requiring attention
                    </Typography>
                    <Typography variant="h5">
                      {summary ? 
                        (summary.totalActiveAccounts - 
                          (summary.totalActiveAccounts - 
                            (summary.totalDue > 0 ? 2 : 0))) : 0}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/credit-control')}
                    >
                      View Details
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Changelog Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Latest Updates
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {changelog.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {changelog.map((entry) => {
              const chipColor = getChipColor(entry.type);
              return (
                <Card key={entry.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="h6" component="h3">
                          {entry.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.date)} • Version {entry.version}
                        </Typography>
                      </Box>
                      <Chip
                        label={entry.type}
                        size="small"
                        sx={{
                          backgroundColor: chipColor.bg,
                          color: chipColor.color,
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                    <Typography variant="body2">{entry.description}</Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No updates available at this time.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Home;
